/**
 * The orbit camera both 3D viewers share.
 *
 * Extracted when the mesh viewer arrived rather than copied into it. Two
 * cameras that drift apart are two viewers whose controls behave differently
 * on the same data, and the near/far derivation below is exactly the sort of
 * detail that gets fixed in one copy and not the other.
 *
 * No matrix library, for the same reason there is no glTF loader: a perspective
 * and a look-at are twenty lines each, and a dependency here would be carried
 * by every page that shows a model.
 */

/** Fixed-length so the compiler knows every component exists. */
export type Vec3 = [number, number, number];

export interface Camera {
  /** Radians. Azimuth around the vertical axis. */
  theta: number;
  /** Radians. Elevation, clamped short of the poles to keep `up` meaningful. */
  phi: number;
  distance: number;
  target: Vec3;
}

export function compile(
  gl: WebGLRenderingContext,
  type: number,
  source: string,
): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("shader oluşturulamadı");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(log ?? "shader derlenemedi");
  }
  return shader;
}

export function normalise(v: Vec3): Vec3 {
  const length = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / length, v[1] / length, v[2] / length];
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/** Where the camera sits, given where it is looking from and how far. */
export function eyeOf(camera: Camera): Vec3 {
  const cosPhi = Math.cos(camera.phi);
  return [
    camera.target[0] + camera.distance * cosPhi * Math.sin(camera.theta),
    camera.target[1] + camera.distance * Math.sin(camera.phi),
    camera.target[2] + camera.distance * cosPhi * Math.cos(camera.theta),
  ];
}

/** Column-major perspective * view, built by hand to avoid a matrix library. */
export function mvpMatrix(
  camera: Camera,
  aspect: number,
  radius: number,
): Float32Array {
  const eye = eyeOf(camera);

  const fz = normalise([
    camera.target[0] - eye[0],
    camera.target[1] - eye[1],
    camera.target[2] - eye[2],
  ]);
  const sx = normalise(cross(fz, [0, 1, 0]));
  const uy = cross(sx, fz);

  // Near and far follow the model's own scale: reconstruction units are
  // arbitrary, so fixed planes would clip one capture and z-fight another.
  const near = Math.max(radius * 0.001, 1e-4);
  const far = radius * 100;
  const fov = (50 * Math.PI) / 180;
  const f = 1 / Math.tan(fov / 2);
  const rangeInv = 1 / (near - far);

  const view = new Float32Array([
    sx[0], uy[0], -fz[0], 0,
    sx[1], uy[1], -fz[1], 0,
    sx[2], uy[2], -fz[2], 0,
    -dot(sx, eye), -dot(uy, eye), dot(fz, eye), 1,
  ]);
  const projection = new Float32Array([
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (near + far) * rangeInv, -1,
    0, 0, near * far * rangeInv * 2, 0,
  ]);

  // Both operands are exactly sixteen long by construction, so `?? 0` is a
  // formality for the type checker rather than a fallback that can fire.
  const at = (m: Float32Array, i: number) => m[i] ?? 0;
  const out = new Float32Array(16);
  for (let c = 0; c < 4; c += 1) {
    for (let r = 0; r < 4; r += 1) {
      out[c * 4 + r] =
        at(projection, r) * at(view, c * 4) +
        at(projection, 4 + r) * at(view, c * 4 + 1) +
        at(projection, 8 + r) * at(view, c * 4 + 2) +
        at(projection, 12 + r) * at(view, c * 4 + 3);
    }
  }
  return out;
}
